import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink, useLocation } from 'react-router-dom';
// material
import { styled, alpha } from '@mui/material/styles';
import { Box, Link, Drawer, Avatar, Chip, Typography } from '@mui/material';
// hooks
import useResponsive from '../../hooks/useResponsive';
// components
import Logo from '../../components/Logo';
import Scrollbar from '../../components/Scrollbar';
import NavSection from '../../components/NavSection';
//
import { hasPermission } from '../../utils/hasPermission';
import navConfig from './NavConfig';

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;

// ── Sidebar wrapper ──────────────────────────────────────────────────────
const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    width: DRAWER_WIDTH,
  },
}));

// ── Business account card ────────────────────────────────────────────────
const AccountCard = styled(Link)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5, 2),
  borderRadius: 14,
  backgroundColor: alpha(theme.palette.primary.main, 0.06),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
  textDecoration: 'none',
  transition: theme.transitions.create(['background-color', 'border-color']),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    borderColor: alpha(theme.palette.primary.main, 0.2),
  },
}));

// ── Sidebar paper overrides ──────────────────────────────────────────────
const drawerPaperSx = (theme) => ({
  width: DRAWER_WIDTH,
  bgcolor: 'background.paper',
  borderRight: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  boxShadow: 'none',
});

// ----------------------------------------------------------------------

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }) {
  const { pathname } = useLocation();
  const isDesktop = useResponsive('up', 'lg');
  const { user, activeApp } = useSelector((state) => state.auth);

  let selectedApp = activeApp;
  if (!selectedApp) {
    try {
      const bootstrapped = sessionStorage.getItem('bootstrappedApp');
      if (bootstrapped) {
        selectedApp = JSON.parse(bootstrapped);
      } else {
        const storedActive = localStorage.getItem('activeApp');
        if (storedActive) {
          selectedApp = JSON.parse(storedActive);
        }
      }
    } catch (e) {
      // ignore storage error
    }
  }

  const businessName = selectedApp?.name || 'Admin App';
  const businessLogo = selectedApp?.branding?.logoUrl || '';

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const isOwner =
    !selectedApp ||
    selectedApp.owner === user?._id ||
    selectedApp.owner === user?.id ||
    (selectedApp.owner &&
      typeof selectedApp.owner === 'object' &&
      (selectedApp.owner._id === user?._id || selectedApp.owner.id === user?._id));

  const filteredNavConfig = navConfig.filter((item) => {
    if (!item) return false;
    if (item?.isOwnerOnly && !isOwner) {
      return false;
    }
    return !item?.permission || hasPermission(user, item?.permission);
  });

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* ── Logo strip ────────────────────────────────────────────────── */}
      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Logo logoUrl={selectedApp?.branding?.logoUrl || '/images/logo.png'} />
        <Chip
          label="Admin"
          size="small"
          color="primary"
          variant="soft"
          sx={{
            height: 22,
            fontSize: '0.65rem',
            fontWeight: 700,
            letterSpacing: '0.04em',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
            color: 'primary.main',
            border: 'none',
          }}
        />
      </Box>

      {/* ── Business card ─────────────────────────────────────────────── */}
      <Box sx={{ px: 2.5, mb: 3 }}>
        <AccountCard underline="none" component={RouterLink} to="#">
          <Avatar src={businessLogo} alt={businessName} sx={{ width: 38, height: 38, borderRadius: 2 }} />
          <Typography variant="subtitle2" fontWeight={600} lineHeight={1.2}>
            {businessName}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize', lineHeight: 1.2 }}>
            {user?.role}
          </Typography>
        </AccountCard>
      </Box>

      {/* ── Navigation ────────────────────────────────────────────────── */}
      <NavSection navConfig={filteredNavConfig} />

      <Box sx={{ flexGrow: 1 }} />
    </Scrollbar>
  );

  return (
    <RootStyle>
      {/* ── Mobile drawer ─────────────────────────────────────────────── */}
      {!isDesktop && (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: (theme) => ({
              ...drawerPaperSx(theme),
              // Rounded right edge for mobile slide-in
              borderTopRightRadius: 20,
              borderBottomRightRadius: 20,
            }),
          }}
        >
          {renderContent}
        </Drawer>
      )}

      {/* ── Desktop persistent drawer ──────────────────────────────────── */}
      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          PaperProps={{
            sx: drawerPaperSx,
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </RootStyle>
  );
}
